import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { createCommunityUpdate, deleteCommunityUpdate, getCommunityByIdOrSlug, getCommunityMembers, getCommunityPage, getCommunityUpdates, likeCommunityUpdate, pinCommunityUpdate, saveCommunityUpdate, unlikeCommunityUpdate, unpinCommunityUpdate, unsaveCommunityUpdate, updateCommunityPage, updateCommunityUpdate, uploadCommunityCoverPicture, uploadCommunityProfilePicture } from '../api/communities'; // Adjust the import path as needed
import usePost from '../hooks/usePost';
import { Loader } from 'lucide-react';

// Create context
export const CommunityContext = createContext(null);

export const CommunityProvider = ({ children }) => {
  const { slug } = useParams(); // Get slug from URL
  const [community, setCommunity] = useState(null);
  const [members, setMembers] = useState(null);
  const [page, setPage] = useState(null);
  const [admins, setAdmins] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loading2, setLoading2] = useState({
    profile: false,
    profilePicture: false,
    coverPicture: false,
    username: false,
    page: false
  });
  const [error2, setError2] = useState({
    profile: '',
    profilePicture: '',
    coverPicture: '',
    username: '',
    page: ''
  });
  const [error, setError] = useState(null);
  const [updates, setUpdates] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({
    profilePicture: 0,
    coverPicture: 0
  });

  const postCtx = usePost();

  // Function to fetch community data
  const fetchCommunityData = useCallback(async () => {
    if (!slug) return;

    try {
      setLoading(true);
      const response = await getCommunityByIdOrSlug(slug);
      setCommunity(response.data.data);
      console.log(response.data.data);
      if(response.data.data.userRole === "owner") {
        setIsOwner(true);
      }
      else
      {
        setIsOwner(false);
      }
      postCtx.fetchCommunityPosts(response.data.data.community._id, {});
      setError(null);
    } catch (err) {
      console.error('Error fetching community:', err);
      setError('Failed to load community');
    } finally {
      setLoading(false);
    }

  }, [slug]);

  const fetchCommunityUpdates = useCallback(async () => {
    if (!community) {
      return;
    }
    try {
      setLoading(true);
      const response = await getCommunityUpdates(community.community._id);
      setUpdates(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching community updates:', err);
      setError('Failed to load community updates');
    } finally {
      setLoading(false);
    }
  }, [community]);

  const fetchCommunityPage = useCallback(async () => {
    if (!community) {
      return;
    }
    try {
      setLoading(true);
      const response = await getCommunityPage(community.community._id);
      console.log(response.data);
      setPage(response.data.data.page);
      setError(null);
    } catch (err) {
      console.error('Error fetching community page:', err);
      setError('Failed to load community page');
    } finally {
      setLoading(false);
    }
  }, [community]);

  const fetchCommunityExtras = useCallback(async () => {
    if (!community) {
      return;
    }
    try {
      setLoading(true);
      // @TODO isOnline only
      const response = await getCommunityMembers(community.community._id);
      setMembers(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching community:', err);
      setError('Failed to load community');
    } finally {
      setLoading(false);
    }
  }, [community])

  const uploadProfileImage = async (image) => {
    if (!community) {
      return;
    }
    try {
      setLoading(prev => ({ ...prev, profilePicture: true }));
      setError(prev => ({ ...prev, profilePicture: '' }));
      setUploadProgress(prev => ({ ...prev, profilePicture: 0 }));
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(image.type)) {
        throw new Error('File type not supported. Please use JPEG, PNG, or WebP format.');
      }

      // Validate file size (max 2MB)
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      if (image.size > maxSize) {
        throw new Error('File size too large. Maximum size is 2MB.');
      }

      const onProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(prev => ({ ...prev, profilePicture: percentCompleted }));
      };

console.log("I : ", image)
      const response = await uploadCommunityProfilePicture(community.community._id, image, onProgress);
      const updatedCommunity = response.data.data.community;
      // onUserUpdate && onUserUpdate(updatedUser);
      fetchCommunityData();
      return updatedCommunity;
    } catch (err) {
      console.error('Error uploading community image:', err);
      setError('Failed to upload community image');
    } finally {
      setLoading(false);
    }
  }


  const uploadCoverImage = async (image) => {
    if (!community) {
      return;
    }
    try {
      setLoading(prev => ({ ...prev, coverPicture: true }));
      setError(prev => ({ ...prev, coverPicture: '' }));
      setUploadProgress(prev => ({ ...prev, coverPicture: 0 }));
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(image.type)) {
        throw new Error('File type not supported. Please use JPEG, PNG, or WebP format.');
      }

      // Validate file size (max 2MB)
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      if (image.size > maxSize) {
        throw new Error('File size too large. Maximum size is 2MB.');
      }

      const onProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(prev => ({ ...prev, coverPicture: percentCompleted }));
      };


      const response = await uploadCommunityCoverPicture(community.community._id, image, onProgress);
      const updatedCommunity = response.data.data.community;
      // onUserUpdate && onUserUpdate(updatedUser);
      fetchCommunityData();
      return updatedCommunity;
    } catch (err) {
      console.error('Error uploading community image:', err);
      setError('Failed to upload community image');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchCommunityExtras();
  }, [fetchCommunityExtras]);

  useEffect(() => {
    fetchCommunityUpdates();
  }, [fetchCommunityUpdates]);

  // Fetch on mount or when slug changes
  useEffect(() => {
    fetchCommunityData();
  }, [fetchCommunityData]);

  useEffect(() => {
    fetchCommunityPage();
  }, [fetchCommunityPage]);

  const createUpdate = (data) => createCommunityUpdate(community.community._id, data);
  const updateUpdate = (updateId, data) => updateCommunityUpdate(community.community._id, updateId, data);
  const deleteUpdate = (updateId) => deleteCommunityUpdate(community.community._id, updateId);
  const pinUpdate = (updateId) => pinCommunityUpdate(community.community._id, updateId);
  const unpinUpdate = (updateId) => unpinCommunityUpdate(community.community._id, updateId);
  const likeUpdate = (updateId) => likeCommunityUpdate(community.community._id, updateId);
  const unlikeUpdate = (updateId) => unlikeCommunityUpdate(community.community._id, updateId);
  const saveUpdate = (updateId) => saveCommunityUpdate(community.community._id, updateId);
  const unsaveUpdate = (updateId) => unsaveCommunityUpdate(community.community._id, updateId);


  const updatePage = (data) => updateCommunityPage(community.community._id, data);
  const fetchPage = () => getCommunityPage(community.community._id);


  const contextValue = {
    community,
    members,
    loading,
    error,
    updates,
    page,
    uploadProgress,
    uploadProfileImage,
    uploadCoverImage,
    isOwner: isOwner,
    loading2,
    refreshCommunity: fetchCommunityData,
    createUpdate,
    updateUpdate,
    deleteUpdate,
    pinUpdate,
    unpinUpdate,
    likeUpdate,
    unlikeUpdate,
    saveUpdate,
    unsaveUpdate,
    updatePage,
    fetchPage
  };

  return (
    <CommunityContext.Provider value={contextValue}>
      {community ? (
        children
      ) : (
        <div className="flex items-center justify-center h-[70vh]">
          {loading ? (
            <div className="flex flex-col items-center gap-4 text-gray-600">
              <Loader className="w-10 h-10 animate-spin" />
              <p className="text-lg">Loading community...</p>
            </div>
          ) : (
            <div className="text-center text-gray-500 space-y-2">
              <h2 className="text-2xl font-semibold text-gray-700">Community Not Found</h2>
              <p>This community doesn’t exist or has been removed.</p>
            </div>
          )}
        </div>
      )}
    </CommunityContext.Provider>
  );
  
};
export const useCommunity = () => useContext(CommunityContext);

export default CommunityProvider;
